import { cn } from "cn";
import { isEmpty } from "lodash-es";
import {
  BwCalendarCheckLine,
  BwCalendarForwardLine,
  BwCheckCircleFill,
  BwCheckCircleLine,
  BwClockOffSleepFill,
  BwClockOffSleepLine,
  BwCloseCircleFill,
  BwCloseCircleLine,
  BwDashCircleLine,
  BwDollarBadgeLine,
  BwFeatherLine,
  BwPenRulerLine,
  BwSearchSquareTitleLine,
  BwTimeCircleLine,
  BwTimeDashLine,
  BwUpdateLeftLine,
} from "@/components/ui/icons";
import { OrderStatus } from "@/types/openapi-enum";

export enum IconType {
  Line = "line",
  Fill = "fill",
}

export const getStatusSpecs = (status: OrderStatus, type = IconType.Line) => {
  let label = "";
  let iconColor = "";
  let badgeColor = "";
  let Icon: any;

  switch (status) {
    // case OrderStatus.Proposed:
    //   label = "Proposed";
    //   iconColor = "text-text-warning";
    //   badgeColor = "orange";
    //   Icon = BwClipboardCheckLine;
    //   break;
    case OrderStatus.Proposed:
      label = "Future";
      iconColor = "text-text-warning";
      badgeColor = "orange";
      Icon = BwCalendarForwardLine;
      break;
    case OrderStatus.Reviewing:
      label = "Reviewing";
      iconColor = "text-text-dark-max";
      badgeColor = "black";
      Icon = BwSearchSquareTitleLine;
      break;
    case OrderStatus.Quoting:
      label = "Quoting";
      iconColor = "text-text-amber";
      badgeColor = "amber";
      Icon = BwPenRulerLine;
      break;
    case OrderStatus.Waiting:
      label = "Waiting";
      iconColor = "text-text-warning";
      badgeColor = "orange";
      Icon = BwTimeCircleLine;
      break;
    case OrderStatus.Scheduling:
      label = "Unscheduled";
      iconColor = "text-text-blue";
      badgeColor = "blue";
      Icon = BwTimeDashLine;
      break;
    case OrderStatus.Scheduled:
      label = "Scheduled";
      iconColor = "text-text-cyan";
      badgeColor = "cyan";
      Icon = BwCalendarCheckLine;
      break;
    case OrderStatus.InProgress:
      label = "In Progress";
      iconColor = "text-text-primary";
      badgeColor = "purple";
      Icon = BwUpdateLeftLine;
      break;
    case OrderStatus.Billing:
      label = "Billing";
      iconColor = "text-text-lime";
      badgeColor = "lime";
      Icon = BwDollarBadgeLine;
      break;
    case OrderStatus.Done:
      label = "Done";
      iconColor = "text-text-success";
      badgeColor = "green";
      Icon = type === IconType.Line ? BwCheckCircleLine : BwCheckCircleFill;
      break;
    case OrderStatus.Deferred:
      label = "Deferred";
      iconColor = "text-text-yellow";
      badgeColor = "yellow";
      Icon = type === IconType.Line ? BwClockOffSleepLine : BwClockOffSleepFill;
      break;
    case OrderStatus.Cancelled:
      label = "Cancelled";
      iconColor = "text-text-negative";
      badgeColor = "red";
      Icon = type === IconType.Line ? BwCloseCircleLine : BwCloseCircleFill;
      break;
    case OrderStatus.Draft:
      label = "Draft";
      iconColor = "text-text-dark-max";
      badgeColor = "neutral";
      Icon = BwFeatherLine;
      break;

    //OrderStatus.New is default
    default:
      label = "New";
      iconColor = "text-text-fuchsia";
      badgeColor = "fuchsia";
      Icon = BwDashCircleLine;
  }

  return {
    label,
    iconColor,
    badgeColor,
    Icon,
  };
};

/**
 * Badge **text** can come from API `customerLabel` while **icon/color** normally follow `statusKey`.
 * When those disagree (e.g. `statusKey=waiting` but `customerLabel="Reviewing"`), pick visuals from the
 * built-in status whose default label matches `customerLabel` (case-insensitive). If no match, keep `statusKey`.
 */
export function resolveOrderStatusForBadgeVisual(
  statusKey: OrderStatus,
  customerLabel?: string | null,
): OrderStatus {
  const trimmed = customerLabel?.trim();
  if (!trimmed) return statusKey;

  const lower = trimmed.toLowerCase();
  for (const key of Object.values(OrderStatus)) {
    const s = key as OrderStatus;
    if (getStatusSpecs(s).label.toLowerCase() === lower) {
      return s;
    }
  }
  return statusKey;
}

export function OrderStatusIcon({
  status,
  type = IconType.Line,
  size = 16,
  className,
}: {
  status: OrderStatus;
  type?: IconType;
  size?: number;
  className?: string;
}) {
  if (isEmpty(status)) {
    return null;
  }
  const { iconColor, Icon } = getStatusSpecs(status, type);

  return <Icon size={size} className={cn(iconColor, "shrink-0", className)} />;
}
